import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { useTranslation } from "../i18n";
import { apiUrl } from "../api";
import "./ImageUpload.css";

const MAX_MEDIA_SIZE_BYTES = 100 * 1024 * 1024;

const ImageUpload = forwardRef(({
  onUploadComplete,
  isMobileViewport = false,
  hasDockActions = false,
  viewerSessionId = "",
}, ref) => {
  const { t } = useTranslation();
  const uploadSubtitle = t("imageUpload.subtitle");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedPreviewItems, setUploadedPreviewItems] = useState([]);
  const [trackedUploadIds, setTrackedUploadIds] = useState([]);
  const sectionRef = useRef(null);
  const libraryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const clearUploadFeedback = useCallback(() => {
    setStatus(null);
    setUploadProgress(0);
    setTrackedUploadIds([]);
    setUploadedPreviewItems((currentItems) => {
      currentItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
  }, []);

  useEffect(() => {
    return () => {
      uploadedPreviewItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [uploadedPreviewItems]);

  useEffect(() => {
    if (trackedUploadIds.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    const pollStatuses = async () => {
      try {
        const params = new URLSearchParams();
        trackedUploadIds.forEach((id) => params.append("image_ids", id));
        const response = await fetch(apiUrl(`/api/gallery/uploads/status?${params.toString()}`));
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success || isCancelled) {
          return;
        }

        const uploads = result.uploads || [];
        const queuedCount = uploads.filter((item) => item.processingStatus === "queued").length;
        const processingCount = uploads.filter((item) => item.processingStatus === "processing").length;
        const failedUploads = uploads.filter((item) => item.processingStatus === "failed");
        const processedCount = uploads.filter((item) => item.processingStatus === "processed").length;

        if (failedUploads.length > 0) {
          const firstFailed = failedUploads[0];
          setStatus({
            type: "error",
            message: firstFailed.processingError || t("imageUpload.processingFailed"),
          });
          setTrackedUploadIds([]);
          return;
        }

        if (queuedCount > 0) {
          setStatus({ type: "info", message: t("imageUpload.queueing") });
          return;
        }

        if (processingCount > 0) {
          setStatus({ type: "info", message: t("imageUpload.processing") });
          return;
        }

        if (processedCount === uploads.length && uploads.length > 0) {
          setStatus({ type: "success", message: t("imageUpload.success") });
          setTrackedUploadIds([]);
        }
      } catch (error) {
        console.error("Gallery upload status error:", error);
      }
    };

    pollStatuses();
    const intervalId = window.setInterval(pollStatuses, 2500);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [t, trackedUploadIds]);

  const uploadFiles = async (files) => {
    const mediaFiles = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );
    if (mediaFiles.length === 0) {
      setStatus({ type: "error", message: t("imageUpload.noMedia") });
      return;
    }

    const oversizedFile = mediaFiles.find((file) => file.size > MAX_MEDIA_SIZE_BYTES);
    if (oversizedFile) {
      setStatus({
        type: "error",
        message: t("imageUpload.fileTooLarge").replace("{filename}", oversizedFile.name),
      });
      return;
    }

    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    const formData = new FormData();
    if (viewerSessionId) {
      formData.append("viewerSessionId", viewerSessionId);
    }
    mediaFiles.forEach((file) => formData.append("files", file));
    const previewItems = mediaFiles.map((file) => ({
      name: file.name,
      mediaType: file.type.startsWith("video/") ? "video" : "image",
      previewUrl: URL.createObjectURL(file),
    }));

    setIsUploading(true);
    setUploadProgress(0);
    setStatus({ type: "info", message: t("imageUpload.queueing") });

    try {
      const result = await new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", apiUrl("/api/gallery/upload"));
        request.responseType = "json";

        request.upload.onprogress = (event) => {
          if (!event.lengthComputable) {
            return;
          }

          const nextProgress = Math.min(
            100,
            Math.round((event.loaded / event.total) * 100)
          );
          setUploadProgress(nextProgress);
          setStatus({
            type: "info",
            message:
              nextProgress < 100
                ? t("imageUpload.uploadingProgress").replace("{percent}", String(nextProgress))
                : t("imageUpload.processing"),
          });
        };

        request.onload = () => {
          const responseBody = request.response || {};
          if (request.status < 200 || request.status >= 300 || !responseBody.success) {
            reject(
              new Error(
                responseBody.detail || responseBody.message || t("imageUpload.error")
              )
            );
            return;
          }
          resolve(responseBody);
        };

        request.onerror = () => {
          reject(new Error(t("imageUpload.error")));
        };

        request.send(formData);
      });

      setStatus({
        type: "success",
        message:
          result.queuePosition > 0
            ? t("imageUpload.successQueued").replace("{count}", String(result.queuePosition))
            : t("imageUpload.success"),
      });
      setTrackedUploadIds(result.uploadedIds || []);
      setUploadedPreviewItems((prevItems) => {
        prevItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        return previewItems;
      });
      onUploadComplete?.({
        uploadedIds: result.uploadedIds || [],
        uploaded: result.uploaded || mediaFiles.length,
      });
    } catch (error) {
      console.error("Gallery upload error:", error);
      previewItems.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      setStatus({
        type: "error",
        message: error.message || t("imageUpload.error"),
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    uploadFiles(Array.from(event.dataTransfer.files));
  };

  const handleFileSelect = (event) => {
    uploadFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  useImperativeHandle(ref, () => ({
    openLibraryPicker() {
      if (!isUploading) {
        libraryInputRef.current?.click();
      }
    },
    openCameraPicker() {
      if (!isUploading) {
        cameraInputRef.current?.click();
      }
    },
    clearUploadFeedback,
  }), [clearUploadFeedback, isUploading]);

  return (
    <div className="upload-section" ref={sectionRef}>
      <div className="upload-header">
        <div className="upload-header-main">
          <div className="upload-header-copy">
            <h2 className="upload-title">{t("imageUpload.title")}</h2>
            {uploadSubtitle && uploadSubtitle !== "imageUpload.subtitle" ? (
              <p className="upload-subtitle">{uploadSubtitle}</p>
            ) : null}
          </div>
          {isMobileViewport && (
            <button
              type="button"
              className="upload-mobile-button"
              onClick={() => libraryInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload size={18} strokeWidth={2} />
              <span>{t("imageUpload.mobileButton")}</span>
            </button>
          )}
        </div>
      </div>

      {!isMobileViewport && (
        <>
          <div
            className={`upload-area ${isDragging ? "dragging" : ""} ${isUploading ? "uploading" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="upload-content">
              <div className="upload-icon">
                {isUploading ? (
                  <div className="upload-spinner"></div>
                ) : (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17,8 12,3 7,8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                )}
              </div>

              <div className="upload-text">
                <p className="upload-main-text">
                  {isUploading ? t("imageUpload.uploading") : t("imageUpload.dragDrop")}
                </p>
                <p className="upload-secondary-text">{t("imageUpload.orClick")}</p>
              </div>

              <input
                ref={libraryInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="upload-input"
                disabled={isUploading}
              />
            </div>
          </div>
        </>
      )}

      {hasDockActions && (
        <div className="upload-mobile-inputs" aria-hidden="true">
          <input
            ref={libraryInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileSelect}
            disabled={isUploading}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            disabled={isUploading}
          />
        </div>
      )}

      {status && (
        <p className={`upload-status upload-status-${status.type}`}>{status.message}</p>
      )}

      {isUploading && (
        <div className="upload-progress" aria-hidden="true">
          <div
            className="upload-progress-bar"
            style={{ width: `${Math.max(uploadProgress, 6)}%` }}
          />
        </div>
      )}

      {uploadedPreviewItems.length > 0 && (
        <div className="upload-preview-section">
          <p className="upload-preview-title">{t("imageUpload.previewTitle")}</p>
          <div className="upload-preview-grid">
            {uploadedPreviewItems.map((item) => (
              <div key={`${item.name}-${item.previewUrl}`} className="upload-preview-card">
                {item.mediaType === "video" ? (
                  <video
                    src={item.previewUrl}
                    className="upload-preview-media"
                    muted
                    playsInline
                    controls
                  />
                ) : (
                  <img
                    src={item.previewUrl}
                    alt={item.name}
                    className="upload-preview-media"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default ImageUpload;
